import { useState, useRef, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import type { DeviceUpdateRecord, DeviceStatus, DeviceSortField } from "@meridian-mdm/shared";
import { bucketCounts } from "@meridian-mdm/shared";
import { fetchJob, retryJob, reinstallDevice } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/ui/toast";
import {
  ArrowLeft,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  Trash2,
} from "lucide-react";

/** Metadata passed to the table so the actions column can reach the handler. */
interface TableMeta {
  onReinstall: (record: DeviceUpdateRecord) => void;
}

// Ordered by operational importance (mirrors STATUS_IMPORTANCE in shared).
const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "All Statuses", value: "" },
  { label: "Timeout", value: "timeout" },
  { label: "Failed", value: "trigger_failed" },
  { label: "Management Failed", value: "management_failed" },
  { label: "Reinstall Failed", value: "reinstall_failed" },
  { label: "Requesting Mgmt", value: "requesting_management" },
  { label: "Reinstalling", value: "reinstalling" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Triggered", value: "triggered" },
  { label: "Triggering", value: "triggering" },
  { label: "Pending", value: "pending" },
  { label: "Up to Date", value: "up_to_date" },
  { label: "Skipped", value: "skipped" },
  { label: "Not Installed", value: "not_installed" },
  { label: "Unenrolled", value: "unenrolled" },
];

const columnHelper = createColumnHelper<DeviceUpdateRecord>();

const columns = [
  columnHelper.accessor("deviceName", {
    header: "Device Name",
    cell: (info) => info.getValue() ?? "--",
  }),
  columnHelper.accessor("serial", {
    header: "Serial",
    cell: (info) => <span className="font-mono text-xs">{info.getValue()}</span>,
  }),
  columnHelper.accessor("versionAtStart", {
    header: "Version at Start",
    cell: (info) => <span className="font-mono text-sm">{info.getValue() ?? "--"}</span>,
  }),
  columnHelper.accessor("currentVersion", {
    header: "Current Version",
    cell: (info) => <span className="font-mono text-sm">{info.getValue() ?? "--"}</span>,
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => <StatusBadge status={info.getValue()} />,
  }),
  columnHelper.accessor("lastError", {
    header: "Last Error",
    enableSorting: false,
    cell: (info) => {
      const val = info.getValue();
      return val ? (
        <span className="max-w-[200px] truncate text-xs text-destructive" title={val}>
          {val}
        </span>
      ) : (
        <span className="text-muted-foreground">--</span>
      );
    },
  }),
  columnHelper.display({
    id: "actions",
    header: "",
    enableSorting: false,
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      return (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Row actions"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <MoreHorizontal className="h-4 w-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => meta.onReinstall(row.original)}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete &amp; Reinstall
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  }),
];

interface StatCardProps {
  label: string;
  value: number;
  className?: string;
}

function StatCard({ label, value, className }: StatCardProps) {
  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

export default function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const numericJobId = Number(jobId);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Default sort: status column, descending (the direction admins prefer in
  // practice — surfaces active states like requesting_management above the bulk).
  const [sorting, setSorting] = useState<SortingState>([{ id: "status", desc: true }]);
  const sortBy = (sorting[0]?.id ?? "status") as DeviceSortField;
  const sortDir = sorting[0]?.desc ? "desc" : "asc";
  // The device row pending a delete/reinstall confirmation (null = dialog closed).
  const [reinstallTarget, setReinstallTarget] = useState<DeviceUpdateRecord | null>(null);

  // Debounce search input — single timer ref, cancelled before each new schedule
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceSearch = useCallback((value: string) => {
    if (searchTimerRef.current !== null) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
      searchTimerRef.current = null;
    }, 300);
  }, []);

  // Cancel any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current !== null) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const {
    data: jobDetail,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["job", numericJobId, page, statusFilter, debouncedSearch, sortBy, sortDir],
    queryFn: () =>
      fetchJob(numericJobId, {
        page,
        limit: 50,
        status: (statusFilter || undefined) as DeviceStatus | undefined,
        search: debouncedSearch || undefined,
        sortBy,
        sortDir,
      }),
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.status === "running" ? 2000 : false;
    },
    enabled: !!numericJobId,
  });

  const retryMutation = useMutation({
    mutationFn: (statuses: ("trigger_failed" | "timeout" | "management_failed")[]) =>
      retryJob(numericJobId, statuses),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["job", numericJobId] });
      toast({ title: "Retry initiated", description: "Devices are being re-queued for update." });
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Retry failed",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const reinstallMutation = useMutation({
    mutationFn: (recordId: number) => reinstallDevice(numericJobId, recordId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["job", numericJobId] });
      toast({
        title: "Reinstall started",
        description: "The device will delete and reinstall the app. This can take a few minutes.",
      });
      setReinstallTarget(null);
    },
    onError: (err) => {
      toast({
        variant: "destructive",
        title: "Reinstall failed to start",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const table = useReactTable({
    data: jobDetail?.records ?? [],
    columns,
    meta: { onReinstall: (record) => setReinstallTarget(record) } satisfies TableMeta,
    state: { sorting },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        // Server-side sort — reset to page 1 so the new ordering starts fresh.
        setPage(1);
        // Never allow "no sort" — fall back to the status-importance default.
        return next.length > 0 ? next : [{ id: "status", desc: true }];
      });
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const isRunning = jobDetail?.status === "running";

  // Live display buckets derived from the per-status summary (the denormalized
  // succeeded/failed columns are only written at completion, so they read 0 for
  // a running job — using them made the UI look stalled).
  const buckets = jobDetail
    ? bucketCounts(jobDetail.summary, jobDetail.skippedDevices, jobDetail.totalDevices)
    : null;

  // Resolved = every terminal outcome (timeout included — the device is done
  // being waited on). In-progress states are the only ones not yet "resolved".
  const completionPct =
    buckets && buckets.total > 0
      ? Math.round(
          ((buckets.succeeded + buckets.failed + buckets.timeout + buckets.skipped) /
            buckets.total) *
            100,
        )
      : 0;

  // Use server-side summary counts (not current-page scan) for retry button state.
  // "Failed" covers hard trigger failures AND management-takeover failures —
  // both are retryable (a management_failed retry re-attempts the takeover).
  const hasFailedRecords =
    (jobDetail?.summary?.trigger_failed ?? 0) + (jobDetail?.summary?.management_failed ?? 0) > 0;
  const hasTimeoutRecords = (jobDetail?.summary?.timeout ?? 0) > 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => void navigate("/jobs")} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Jobs
        </Button>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to load job: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      </div>
    );
  }

  if (!jobDetail || !buckets) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => void navigate("/jobs")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Job #{jobDetail.id}</h1>
            <Badge
              variant="outline"
              className={
                isRunning
                  ? "border-blue-300 bg-blue-100 text-blue-700"
                  : "border-green-300 bg-green-100 text-green-700"
              }
            >
              {isRunning ? "Running" : "Completed"}
            </Badge>
            {isRunning && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
                </span>
                Live
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {jobDetail.appName} &rarr; v{jobDetail.targetVersion}
            {" | "}
            Started {new Date(jobDetail.startedAt).toLocaleString()}
            {jobDetail.triggeredByEmail && ` by ${jobDetail.triggeredByEmail}`}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium">{completionPct}%</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Total Devices" value={buckets.total} />
        <StatCard label="Succeeded" value={buckets.succeeded} className="border-green-200" />
        <StatCard
          label="Failed"
          value={buckets.failed}
          className={buckets.failed > 0 ? "border-red-200" : ""}
        />
        <StatCard
          label="Timeout"
          value={buckets.timeout}
          className={buckets.timeout > 0 ? "border-orange-200" : ""}
        />
        <StatCard label="Skipped" value={buckets.skipped} />
        <StatCard
          label="In Progress"
          value={buckets.inProgress}
          className={isRunning ? "border-blue-200" : ""}
        />
      </div>

      {/* Actions + Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or serial..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              debounceSearch(e.target.value);
            }}
            className="pl-9"
          />
        </div>
        <Select
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="w-40"
        />
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={!hasFailedRecords || retryMutation.isPending}
            onClick={() => retryMutation.mutate(["trigger_failed", "management_failed"])}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry Failed
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            disabled={!hasTimeoutRecords || retryMutation.isPending}
            onClick={() => retryMutation.mutate(["timeout"])}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Retry Timeouts
          </Button>
        </div>
      </div>

      {/* Honesty note: jobs created before per-device skip tracking count
          skipped devices in the totals but have no rows for them. */}
      {jobDetail.skippedDevices - jobDetail.summary.skipped > 0 && !statusFilter && (
        <p className="text-xs text-muted-foreground">
          Note: {jobDetail.skippedDevices - jobDetail.summary.skipped} skipped device
          {jobDetail.skippedDevices - jobDetail.summary.skipped === 1 ? " is" : "s are"} counted in
          the totals above but not listed below — this job predates per-device skip tracking.
        </p>
      )}

      {/* Device records table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ChevronUp className="h-3.5 w-3.5" />
                          ) : sorted === "desc" ? (
                            <ChevronDown className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {jobDetail.pagination && jobDetail.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {jobDetail.pagination.page} of {jobDetail.pagination.totalPages} (
            {jobDetail.pagination.total} records)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= jobDetail.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete & Reinstall confirm dialog */}
      <Dialog
        open={reinstallTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReinstallTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete &amp; reinstall the app?</DialogTitle>
            <DialogDescription className="space-y-2">
              <span className="block">
                This deletes <span className="font-medium">{jobDetail.appName}</span> from{" "}
                <span className="font-medium">
                  {reinstallTarget?.deviceName ?? reinstallTarget?.serial}
                </span>{" "}
                and reinstalls it fresh.{" "}
                <span className="font-medium">Local app data is wiped.</span>
              </span>
              <span className="block">
                It runs in the background and can take several minutes. If the app is unmanaged, MDM
                can&apos;t delete it — you may need to delete it on the device by hand; the system
                will keep watching and reinstall once it&apos;s gone.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReinstallTarget(null)}
              disabled={reinstallMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => reinstallTarget && reinstallMutation.mutate(reinstallTarget.id)}
              disabled={reinstallMutation.isPending}
              className="gap-2"
            >
              <Trash2 className="h-4 w-4" />
              {reinstallMutation.isPending ? "Starting…" : "Delete & Reinstall"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
